import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {Aangifte, BokkzIbDataService, IncomeTaxEntity} from '@snelstart-ventures/fiscaal/domain-logic';
import {FormControl, FormGroup} from '@angular/forms';
import {filter, Observable, shareReplay, take} from 'rxjs';
import {SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {SharedUiAlertModule} from '@snelstart/ui-alert';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiTooltipModule} from '@snelstart/ui-tooltip';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {AsyncPipe} from "@angular/common";

@Component({
  selector: 'snelstart-venture-fiscaal-feature-premieplicht',
  templateUrl: './premieplicht.component.html',
  styleUrls: ['./premieplicht.component.scss'],
  imports: [
    SharedUiFormControlsModule,
    SharedUiAlertModule,
    SharedUiButtonModule,
    SharedUiTooltipModule,
    AsyncPipe
  ]
})
export class PremieplichtComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private dataService = inject(BokkzIbDataService);
  private destroyRef = inject(DestroyRef);
  public isPartner = false;

  public formGroup = new FormGroup({
    periodeNietVerzekerdVoorAOWenANW: new FormGroup({
      begindatum: new FormControl<Date | null>(null),
      einddatum: new FormControl<Date | null>(null),
    }),
    periodeNietVerzekerdVoorWLZenWVZ: new FormGroup({
      begindatum: new FormControl<Date | null>(null),
      einddatum: new FormControl<Date | null>(null),
    }),
    verminderingPremieInkomen: new FormControl<number | null>(null),
    correctiePremieInkomen: new FormControl<number | null>(null),
  });

  readonly incomeTaxEntity$: Observable<IncomeTaxEntity> =
    this.dataService.incomeTaxEntity$.pipe(
      filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  ngOnInit(): void {
    this.dataService.partnerAangifte$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(partner => {
      this.isPartner = partner;
      this.incomeTaxEntity$.pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(entity => {
        if (partner) {
          this.handleSwitch(entity, true);
        } else {
          this.handleSwitch(entity, false);
        }
      });
    });
  }

  ngOnDestroy(): void {
    // rely on saveFrom in component
  }

  private handleSwitch(entity: IncomeTaxEntity, isPartner: boolean): void {
    const details = isPartner ? entity.aangifte?.partnerAangifte : entity.aangifte;
    if (details) {
      this.resetAndPatchForms(details);
    }
  }

  private resetAndPatchForms(details: Aangifte): void {
    this.formGroup.reset({}, { emitEvent: false });
    this.formGroup.patchValue(details.premieplicht ?? {}, { emitEvent: false });
  }

  saveForm(): void {
    const premieplichtValue = this.formGroup.getRawValue();
    const hasPremieplichtValue = this.hasSubmittedValue(premieplichtValue);

    this.dataService.patchIncomeTaxEntity((currentEntity) => {
      if (!currentEntity.aangifte) {
        return currentEntity;
      }

      const update = (aangifte: Aangifte) => {
        const {premieplicht: _ignored, ...aangifteWithoutPremieplicht} = aangifte;
        return hasPremieplichtValue
          ? {
            ...aangifteWithoutPremieplicht,
            premieplicht: premieplichtValue,
          }
          : aangifteWithoutPremieplicht;
      };

      const updatedAangifte = this.isPartner ? {
        ...currentEntity.aangifte,
        partnerAangifte: update(currentEntity.aangifte?.partnerAangifte!),
      } : update(currentEntity.aangifte);

      return {
        ...currentEntity,
        aangifte: updatedAangifte,
      } as IncomeTaxEntity;
    });
  }

  private hasSubmittedValue(value: unknown): boolean {
    if (value === null || value === undefined) {
      return false;
    }

    if (typeof value === 'string') {
      return value.trim().length > 0;
    }

    if (typeof value === 'number') {
      return Number.isFinite(value) && value !== 0;
    }

    if (typeof value === 'boolean') {
      return value;
    }

    if (value instanceof Date) {
      return !Number.isNaN(value.getTime());
    }

    if (Array.isArray(value)) {
      return value.some((item) => this.hasSubmittedValue(item));
    }

    if (typeof value === 'object') {
      return Object.values(value as Record<string, unknown>).some((item) => this.hasSubmittedValue(item));
    }

    return false;
  }
}
